import { useState, useEffect } from 'react';
import { trpc } from '../../lib/trpc.js';
import { getStoredUser } from '../../lib/auth.js';

const tabs = ['Campaigns', 'Flow Builder', 'Buyers', 'Numbers', 'Live Calls'];

interface Campaign {
  id: string;
  name: string;
  vertical?: string;
  status?: string;
  numbersCount?: number;
  callsToday?: number;
  revenue?: number;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-emerald-500/20 text-emerald-400',
    paused: 'bg-amber-500/20 text-amber-400',
    capped: 'bg-rose-500/20 text-rose-400',
    connected: 'bg-emerald-500/20 text-emerald-400',
    ivr: 'bg-blue-500/20 text-blue-400',
    ringing: 'bg-amber-500/20 text-amber-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] ?? 'bg-slate-700 text-slate-300'}`}>
      {status}
    </span>
  );
}

function formatCurrency(val: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
}

function TableSkeleton({ cols }: { cols: number }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden animate-pulse">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="flex gap-4 p-4 border-b border-slate-800/50">
          {[...Array(cols)].map((_, j) => (
            <div key={j} className="h-4 bg-slate-800 rounded flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export default function IVRPage() {
  const [activeTab, setActiveTab] = useState('Campaigns');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    trpc.ivr.listCampaigns.query({ tenantId: user.tenantId })
      .then(res => setCampaigns((res.campaigns ?? []) as Campaign[]))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">📱 IVR Platform</h1>
        <button className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium">
          + New Campaign
        </button>
      </div>

      {/* Tabs */}
      <div className="overflow-x-auto -mx-1">
        <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800 min-w-max mx-1">
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'Campaigns' && (
        loading ? <TableSkeleton cols={6} /> : (
          campaigns.length === 0 ? (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center">
              <div className="text-4xl mb-4">📱</div>
              <h3 className="text-lg font-semibold text-white mb-2">No campaigns yet</h3>
              <p className="text-slate-400 mb-4">Create your first IVR campaign to get started.</p>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm">+ New Campaign</button>
            </div>
          ) : (
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Campaign</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Vertical</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Status</th>
                    <th className="text-right p-4 text-xs font-medium text-slate-400 uppercase">Numbers</th>
                    <th className="text-right p-4 text-xs font-medium text-slate-400 uppercase">Calls Today</th>
                    <th className="text-right p-4 text-xs font-medium text-slate-400 uppercase">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(c => (
                    <tr key={c.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                      <td className="p-4 text-sm font-medium text-white">{c.name}</td>
                      <td className="p-4 text-sm text-slate-300">{c.vertical ?? '—'}</td>
                      <td className="p-4"><StatusBadge status={c.status ?? 'active'} /></td>
                      <td className="p-4 text-sm text-slate-300 text-right">{c.numbersCount ?? 0}</td>
                      <td className="p-4 text-sm text-slate-300 text-right">{c.callsToday ?? 0}</td>
                      <td className="p-4 text-sm text-emerald-400 text-right font-medium">{formatCurrency(c.revenue ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )
        )
      )}

      {activeTab === 'Flow Builder' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 flex flex-col items-center justify-center min-h-[400px]">
          <div className="text-6xl mb-4">🔧</div>
          <h3 className="text-xl font-semibold text-white mb-2">Visual IVR Flow Builder</h3>
          <p className="text-slate-400 text-center max-w-md mb-4">
            Drag-and-drop nodes to create IVR flows. Connect greetings, menus, AI questions, and transfer nodes.
          </p>
          <div className="flex gap-3">
            {['Greeting', 'Menu', 'AI Question', 'Transfer', 'Hangup'].map(node => (
              <div key={node} className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-300 cursor-grab">
                {node}
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-6">React Flow integration coming soon</p>
        </div>
      )}

      {activeTab === 'Buyers' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center">
          <div className="text-4xl mb-4">👥</div>
          <h3 className="text-lg font-semibold text-white mb-2">Buyer Management</h3>
          <p className="text-slate-400 mb-2">Configure buyers, set bids, manage daily caps and quality scores.</p>
          <p className="text-slate-500 text-xs">Buyer configuration coming soon — manage buyers in the Network module.</p>
        </div>
      )}

      {activeTab === 'Numbers' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center">
          <div className="text-4xl mb-4">📞</div>
          <h3 className="text-lg font-semibold text-white mb-2">Phone Number Management</h3>
          <p className="text-slate-400 mb-4">Search Telnyx inventory, provision numbers, assign to campaigns</p>
          <button className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm">Search Numbers</button>
        </div>
      )}

      {activeTab === 'Live Calls' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center">
          <div className="w-2 h-2 bg-slate-600 rounded-full inline-block mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Live Call Monitor</h3>
          <p className="text-slate-400 text-sm">No active calls at this time.</p>
          <p className="text-xs text-slate-500 mt-2">Live call tracking coming soon.</p>
        </div>
      )}
    </div>
  );
}
